import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box, Button, Card, CardContent, Chip, Divider, Grid, Paper,
  TextField, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Alert, Dialog, DialogTitle, DialogContent,
  DialogActions, MenuItem, Tabs, Tab, Autocomplete,
} from '@mui/material';
import {
  Phone, Place, Warning, Send, Refresh, MyLocation, AddCircle, Map as MapIcon, ReportProblem, OpenInNew, Description,
} from '@mui/icons-material';
import api from '../../api/client';
import { escapeHtml, safeColor, jsonForScript } from '../../utils/html';
import { clearSession } from '../../api/session';
import useCADWebSocket, { SoundConfig } from '../../hooks/useCADWebSocket';
import { formatTime24, formatDateShort, ensureTimezone } from '../../utils/time';
import { startAvl, stopAvl } from '../../native/avl';

interface UnitInfo {
  id: number; callsign: string; unit_type: string; agency_name: string;
  status_id: number; status_name: string; status_color: string;
  location_lat?: number | null; location_lng?: number | null;
}
interface PremiseDocument {
  id: number; category: string; notes: string; original_filename: string;
  content_type: string; size_bytes: number; uploaded_by: string;
  uploaded_at: string; view_url: string;
}
const DOC_CATEGORY_LABELS: Record<string, string> = {
  building_diagram: 'Building Diagram',
  hazard_plan: 'Hazard Plan',
  emergency_contacts: 'Emergency Contacts',
  other: 'Other',
};
interface CallInfo {
  id: number; cad_number: string; call_type_code: string; call_type_name: string;
  priority: number; status: string; location_address: string; location_lat: number | null;
  location_lng: number | null; location_apt: string; cross_street: string;
  reporting_party: string; reporting_phone: string; narrative: string;
  created_at: string; dispatched_at: string | null;
  location_id?: number | null; premise_documents?: PremiseDocument[];
}
interface StatusOption { id: number; code: string; name: string; color: string; }
interface RecentCall {
  id: number; cad_number: string; call_type_code: string; call_type_name: string;
  priority: number; status: string; location_address: string; created_at: string;
}
interface Dashboard {
  user: { id: number; username: string; full_name: string };
  personnel_id: number | null;
  assigned_unit: UnitInfo | null;
  assigned_call: CallInfo | null;
  available_statuses: StatusOption[];
}
interface CallTypeOption { id: number; code: string; name: string; available_for_field_incident: boolean; }

const PRIORITY_COLORS: Record<number, string> = {
  1: '#f44336', 2: '#ff9800', 3: '#2196f3', 4: '#4caf50', 5: '#9e9e9e',
};

const STATUS_BTN_SIZE = 72;

export default function MdtPage() {
  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [recentCalls, setRecentCalls] = useState<RecentCall[]>([]);
  const [note, setNote] = useState('');
  const [error, setError] = useState('');

  // Field incident state
  const [fieldDialogOpen, setFieldDialogOpen] = useState(false);
  const [callTypes, setCallTypes] = useState<CallTypeOption[]>([]);
  const [fieldForm, setFieldForm] = useState({ call_type_id: 0, priority: 3, location_address: '', location_apt: '', location_lat: null as number | null, location_lng: null as number | null, location_id: null as number | null, narrative: '' });
  const [fieldError, setFieldError] = useState('');
  const [locationResults, setLocationResults] = useState<any[]>([]);
  const locationTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Auto-locate state
  const [locating, setLocating] = useState(false);
  const [locateResult, setLocateResult] = useState('');

  // Clear/disposition dialog state
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [dispositions, setDispositions] = useState<{id: number; code: string; name: string}[]>([]);
  const [selectedDisposition, setSelectedDisposition] = useState('');
  const [pendingClearCallId, setPendingClearCallId] = useState<number | null>(null);
  const [pendingClearCadNumber, setPendingClearCadNumber] = useState('');
  const [fieldConfirmOpen, setFieldConfirmOpen] = useState(false);
  const [fieldConfirmCad, setFieldConfirmCad] = useState('');
  const [fieldConfirmCountdown, setFieldConfirmCountdown] = useState(5);

  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  // Units & Calls data for tabs
  const [allUnits, setAllUnits] = useState<any[]>([]);
  const [allCalls, setAllCalls] = useState<any[]>([]);
  const [selectedCallDetail, setSelectedCallDetail] = useState<any>(null);
  const [callDetailOpen, setCallDetailOpen] = useState(false);

  const loadDashboard = useCallback(() => {
    api.get('/mobile/dashboard').then((r) => setDashboard(r.data))
      .catch((err) => setError(err.response?.data?.detail || 'Failed to load dashboard'));
    api.get('/mobile/calls/recent').then((r) => setRecentCalls(r.data)).catch(() => {});
  }, []);

  const loadTabData = useCallback(() => {
    api.get('/units').then((r) => setAllUnits(r.data)).catch(() => {});
    api.get('/calls').then((r) => setAllCalls(r.data)).catch(() => {});
  }, []);

  useEffect(() => { ensureTimezone(); loadDashboard(); loadTabData(); }, [loadDashboard, loadTabData]);

  // Native app: report device GPS as AVL while on duty (server drops it when
  // the unit is off duty). Stops when leaving the MDT screen.
  useEffect(() => {
    void startAvl();
    return () => { void stopAvl(); };
  }, []);

  const [soundConfig, setSoundConfig] = useState<SoundConfig>({});

  useEffect(() => {
    api.get('/system-config/public').then((r) => {
      setSoundConfig({
        sound_mobile_call_assigned: r.data.sound_mobile_call_assigned,
        sound_mobile_call_update: r.data.sound_mobile_call_update,
      });
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!fieldConfirmOpen) return;
    if (fieldConfirmCountdown <= 0) return;
    const t = setTimeout(() => setFieldConfirmCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [fieldConfirmOpen, fieldConfirmCountdown]);

  const handleMdtLogout = useCallback(() => {
    void stopAvl();
    clearSession();
    window.location.href = '/mdt';
  }, []);

  const onRawEvent = useCallback((event: string, data: any) => {
    if (event === 'unit_logoff' && dashboard?.assigned_unit && data?.unit_id === dashboard.assigned_unit.id) {
      handleMdtLogout();
    }
  }, [dashboard, handleMdtLogout]);

  const onWsUpdate = useCallback(() => { loadDashboard(); loadTabData(); }, [loadDashboard, loadTabData]);

  useCADWebSocket(onWsUpdate, 'mobile', soundConfig, onRawEvent);

  const logoffUnit = async () => {
    if (!window.confirm('Are you sure you want to log off duty?')) return;
    try {
      await api.post('/mobile/logoff-unit');
    } catch { /* ignore — will redirect anyway */ }
    handleMdtLogout();
  };

  const changeStatus = async (statusId: number) => {
    if (!dashboard?.assigned_unit) return;
    // If changing to AVL and unit has an active call, clear from call first
    const targetStatus = dashboard.available_statuses.find((s: any) => s.id === statusId);
    if (targetStatus?.code === 'AVL' && dashboard.assigned_call) {
      try {
        const r = await api.post(`/mobile/unit/${dashboard.assigned_unit.id}/clear`);
        if (r.data.is_last_unit && r.data.call_id) {
          setPendingClearCallId(r.data.call_id);
          setPendingClearCadNumber(r.data.cad_number || '');
          setSelectedDisposition('');
          api.get('/dispositions').then((dr) => setDispositions(dr.data)).catch(() => {});
          setClearDialogOpen(true);
          loadDashboard();
          return;
        }
      } catch { /* ignore */ }
    }
    await api.patch(`/mobile/unit/${dashboard.assigned_unit.id}/status`, { status_id: statusId });
    loadDashboard();
  };

  const handleCloseCall = async () => {
    if (!pendingClearCallId || !selectedDisposition) return;
    try {
      await api.post(`/mobile/call/${pendingClearCallId}/close`, {
        disposition: selectedDisposition,
      });
    } catch { /* ignore */ }
    setClearDialogOpen(false);
    loadDashboard();
  };

  const handleLeaveCallOpen = () => {
    setClearDialogOpen(false);
    loadDashboard();
  };

  const addNote = async () => {
    if (!note.trim() || !dashboard?.assigned_call) return;
    await api.post(`/mobile/call/${dashboard.assigned_call.id}/note`, { note });
    setNote('');
    loadDashboard();
  };

  const handleAutoLocate = async () => {
    if (!dashboard?.assigned_unit) return;
    setLocating(true);
    setLocateResult('');
    try {
      const r = await api.get(`/mobile/unit/${dashboard.assigned_unit.id}/location`);
      if (r.data.lat && r.data.lng) {
        setLocateResult(`${r.data.current_location || 'Located'} (${r.data.lat.toFixed(5)}, ${r.data.lng.toFixed(5)})`);
      } else {
        setLocateResult('No TAIP/AVL location available');
      }
    } catch {
      setLocateResult('Failed to get location');
    }
    setLocating(false);
  };

  const openFieldIncident = async () => {
    setFieldError('');
    setFieldForm({ call_type_id: 0, priority: 3, location_address: '', location_apt: '', location_lat: null, location_lng: null, location_id: null, narrative: '' });
    setLocationResults([]);
    try {
      const r = await api.get('/mobile/call-types');
      setCallTypes(r.data);
    } catch { /* ignore */ }
    setFieldDialogOpen(true);
  };

  const submitFieldIncident = async () => {
    if (!fieldForm.call_type_id) { setFieldError('Select a call type'); return; }
    let payload = fieldForm;
    // Geocode a hand-typed address through the shared server geocoder (agency
    // address points / centerlines when the tenant policy uses agency data).
    if (fieldForm.location_address && !fieldForm.location_address.startsWith('@')
        && fieldForm.location_lat == null && fieldForm.location_id == null) {
      try {
        const g = await api.post('/gis/geocode', { address: fieldForm.location_address });
        if (g.data.hit && g.data.lat != null && g.data.lng != null) {
          payload = { ...fieldForm, location_lat: g.data.lat, location_lng: g.data.lng };
        }
      } catch { /* non-fatal — create without coords */ }
    }
    try {
      const r = await api.post('/mobile/field-incident', payload);
      setFieldDialogOpen(false);
      setFieldError('');
      setFieldForm({ call_type_id: 0, priority: 3, location_address: '', location_apt: '', location_lat: null, location_lng: null, location_id: null, narrative: '' });
      loadDashboard();
      setFieldConfirmCad(r.data.cad_number);
      setFieldConfirmCountdown(5);
      setFieldConfirmOpen(true);
    } catch (err: any) {
      setFieldError(err?.response?.data?.detail || 'Failed to create incident');
    }
  };

  const handleEmergency = async () => {
    try {
      await api.post('/mobile/emergency');
    } catch { /* ignore */ }
  };

  const openCallDetail = async (callId: number) => {
    try {
      const r = await api.get(`/calls/${callId}`);
      setSelectedCallDetail(r.data);
      setCallDetailOpen(true);
    } catch { /* ignore */ }
  };

  if (error) return <Box sx={{ p: 3 }}><Alert severity="error">{error}</Alert></Box>;
  if (!dashboard) return <Box sx={{ p: 3 }}><Typography>Loading MDT...</Typography></Box>;

  const { assigned_unit, assigned_call, available_statuses } = dashboard;

  return (
    <Box sx={{ pb: assigned_unit ? `${STATUS_BTN_SIZE + 24}px` : 0 }}>
      {/* Unit info bar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {assigned_unit
            ? <>Unit: <strong>{assigned_unit.callsign}</strong> — {dashboard.user.full_name}</>
            : <>Logged in as: <strong>{dashboard.user.full_name}</strong> ({dashboard.user.username})</>
          }
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {assigned_unit && (
            <>
              <Button size="small" startIcon={<MyLocation />} onClick={handleAutoLocate} disabled={locating}>
                {locating ? 'Locating...' : 'Auto-Locate'}
              </Button>
              <Button variant="outlined" color="success" startIcon={<AddCircle />} onClick={openFieldIncident}
                sx={{ minHeight: 48, fontSize: 16, px: 3 }}>
                Field Incident
              </Button>
              <Button variant="outlined" color="warning" startIcon={<ReportProblem />} onClick={handleEmergency}
                sx={{ minHeight: 48, fontSize: 16, px: 3, fontWeight: 700 }}>
                DC
              </Button>
            </>
          )}
          <Button variant="outlined" startIcon={<Refresh />} onClick={loadDashboard}
            sx={{ minHeight: 48, fontSize: 16, px: 3 }}>
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Tabs */}
      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ minHeight: 48, mb: 1, borderBottom: 1, borderColor: 'divider', '& .MuiTabs-indicator': { height: 3 } }}>
        <Tab label="Main" sx={{ minHeight: 48, py: 1, fontSize: 16, fontWeight: 700 }} />
        <Tab label="Map" sx={{ minHeight: 48, py: 1, fontSize: 16, fontWeight: 700 }} />
        <Tab label="Units" sx={{ minHeight: 48, py: 1, fontSize: 16, fontWeight: 700 }} />
        <Tab label="Calls" sx={{ minHeight: 48, py: 1, fontSize: 16, fontWeight: 700 }} />
      </Tabs>

      {locateResult && (
        <Alert severity="info" sx={{ mb: 1, py: 0 }} onClose={() => setLocateResult('')}>
          {locateResult}
        </Alert>
      )}

      {/* Tab 0: Main */}
      {activeTab === 0 && (
        <>
          {!assigned_unit ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              You are not currently assigned to a unit. Contact dispatch for unit assignment.
            </Alert>
          ) : (
            <Card sx={{ mb: 1, border: `2px solid ${assigned_unit.status_color}` }}>
              <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                <Grid container spacing={1} sx={{ alignItems: 'center' }}>
                  <Grid size={{ xs: 8 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      {assigned_unit.callsign}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {assigned_unit.unit_type.charAt(0).toUpperCase() + assigned_unit.unit_type.slice(1)} — {assigned_unit.agency_name}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 4 }} sx={{ textAlign: 'right' }}>
                    <Chip
                      label={assigned_unit.status_name}
                      sx={{ bgcolor: assigned_unit.status_color, color: '#fff', fontSize: 16, py: 2, px: 1 }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}

          {assigned_call ? (
            <Grid container spacing={1} sx={{ mb: 1, alignItems: 'stretch' }}>
            <Grid size={{ xs: 12, md: 7 }} sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%', border: `2px solid ${PRIORITY_COLORS[assigned_call.priority] || '#9e9e9e'}` }}>
              <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {assigned_call.cad_number}
                  </Typography>
                  <Box>
                    <Chip size="small" label={`P${assigned_call.priority}`}
                      sx={{ bgcolor: PRIORITY_COLORS[assigned_call.priority], color: '#fff', mr: 1 }} />
                    <Chip size="small" label={assigned_call.call_type_code} color="primary" />
                  </Box>
                </Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {assigned_call.call_type_name}
                </Typography>
                <Divider sx={{ my: 0.5 }} />

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <Place sx={{ fontSize: 18, mr: 0.5 }} />
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {assigned_call.location_address}
                    {assigned_call.location_apt ? ` Apt ${assigned_call.location_apt}` : ''}
                  </Typography>
                  {(assigned_call.location_lat && assigned_call.location_lng) && (
                    <Button
                      size="small" variant="outlined" startIcon={<MapIcon />}
                      sx={{ ml: 1, minWidth: 'auto', whiteSpace: 'nowrap' }}
                      onClick={() => {
                        const lat = Number(assigned_call.location_lat);
                        const lng = Number(assigned_call.location_lng);
                        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
                        const addr = assigned_call.location_address || '';
                        const cad = assigned_call.cad_number;
                        const popupJs = jsonForScript(`<b>${escapeHtml(cad)}</b><br/>${escapeHtml(addr)}`);
                        // eslint-disable-next-line no-useless-escape
                        const html = `<!DOCTYPE html><html><head><title>Map - ${escapeHtml(cad)}</title>
                          <link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css"/>
                          <script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"><\/script>
                          <style>body{margin:0}#map{width:100%;height:100vh}</style></head>
                          <body><div id="map"></div><script>
                          var m=L.map('map').setView([${lat},${lng}],16);
                          var st=L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',{maxZoom:20,attribution:'OSM'}).addTo(m);
                          var dk=L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',{maxZoom:20,attribution:'CARTO'});
                          var sat=L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',{attribution:'Esri'});
                          L.control.layers({'Street':st,'Dark':dk,'Satellite':sat}).addTo(m);
                          L.marker([${lat},${lng}]).addTo(m).bindPopup(${popupJs})
                            .openPopup();
                          <\/script></body></html>`;
                        const w = window.open('', `mdt_map_${String(cad).replace(/[^A-Za-z0-9_-]/g, '_')}`, 'width=700,height=500,menubar=no,toolbar=no');
                        if (w) { w.document.write(html); w.document.close(); }
                      }}
                    >
                      Map
                    </Button>
                  )}
                </Box>
                {assigned_call.cross_street && (
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 3 }}>
                    X-Street: {assigned_call.cross_street}
                  </Typography>
                )}
                {assigned_call.reporting_party && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                    <Phone sx={{ fontSize: 18, mr: 0.5 }} />
                    <Typography variant="body2">
                      RP: {assigned_call.reporting_party}
                      {assigned_call.reporting_phone ? ` — ${assigned_call.reporting_phone}` : ''}
                    </Typography>
                  </Box>
                )}

                {assigned_call.narrative && (
                  <>
                    <Divider sx={{ my: 0.5 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Narrative</Typography>
                    <Paper sx={{ p: 1, bgcolor: '#0d0d1a', maxHeight: 200, overflow: 'auto' }}>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', fontSize: 13 }}>
                        {assigned_call.narrative}
                      </Typography>
                    </Paper>
                    {assigned_call.narrative.includes('HAZARD') && (
                      <Alert severity="warning" icon={<Warning />} sx={{ mt: 1 }}>
                        This call has hazard information — review narrative carefully.
                      </Alert>
                    )}
                  </>
                )}

                {assigned_call.premise_documents && assigned_call.premise_documents.length > 0 && (
                  <>
                    <Divider sx={{ my: 0.5 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Description fontSize="small" /> Premise Documents
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {assigned_call.premise_documents.map((d) => (
                        <Button key={d.id} size="small" variant="outlined" startIcon={<OpenInNew />}
                          onClick={() => window.open(d.view_url, '_blank', 'noopener')}>
                          {(DOC_CATEGORY_LABELS[d.category] || 'Document')}{d.notes ? ` — ${d.notes}` : ''}
                        </Button>
                      ))}
                    </Box>
                  </>
                )}

                <Divider sx={{ my: 0.5 }} />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    fullWidth size="small" placeholder="Add a note to this call..."
                    value={note} onChange={(e) => setNote(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && addNote()}
                  />
                  <Button variant="contained" startIcon={<Send />} onClick={addNote} disabled={!note.trim()}>
                    Add
                  </Button>
                </Box>
              </CardContent>
            </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 5 }} sx={{ display: 'flex' }}>
              <Paper sx={{ width: '100%', minHeight: 320, overflow: 'hidden', position: 'relative' }}>
                {(assigned_call.location_lat && assigned_call.location_lng) ? (
                  <MdtCallMap call={assigned_call} unit={assigned_unit} />
                ) : (
                  <Box sx={{ height: '100%', minHeight: 320, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                      No mapped location for this call.
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Grid>
            </Grid>
          ) : assigned_unit ? (
            <Alert severity="info" sx={{ mb: 1 }}>No active call assignment. Standing by.</Alert>
          ) : null}

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Recent Calls</Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>CAD #</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Date/Time</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentCalls.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell
                      sx={{ fontFamily: 'monospace', fontWeight: 700, color: 'primary.main', cursor: 'pointer', textDecoration: 'underline' }}
                      onClick={() => openCallDetail(c.id)}
                    >{c.cad_number}</TableCell>
                    <TableCell>{c.call_type_code}</TableCell>
                    <TableCell>
                      <Chip size="small" label={`P${c.priority}`}
                        sx={{ bgcolor: PRIORITY_COLORS[c.priority], color: '#fff' }} />
                    </TableCell>
                    <TableCell>{c.location_address}</TableCell>
                    <TableCell><Chip size="small" label={c.status.toUpperCase()} /></TableCell>
                    <TableCell>{formatDateShort(c.created_at)} {formatTime24(c.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Tab 1: Map */}
      {activeTab === 1 && (
        <Box sx={{ height: 'calc(100vh - 180px)', position: 'relative' }}>
          <MdtMapView units={allUnits} calls={allCalls} />
        </Box>
      )}

      {/* Tab 2: Units */}
      {activeTab === 2 && (
        <TableContainer component={Paper}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Unit</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Agency</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Current Location</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {allUnits.filter((u: any) => u.is_logged_on).map((u: any) => (
                <TableRow key={u.id} hover>
                  <TableCell sx={{ fontWeight: 700 }}>{u.callsign}</TableCell>
                  <TableCell>{u.unit_type_name || u.unit_type || ''}</TableCell>
                  <TableCell>{u.agency_name || ''}</TableCell>
                  <TableCell>
                    <Chip size="small" label={u.status_name || ''}
                      sx={{ bgcolor: u.status_color || '#666', color: '#fff', fontWeight: 600 }} />
                  </TableCell>
                  <TableCell>
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                      {u.location_updated_at && (
                        <Box component="span" sx={{
                          width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
                          bgcolor: (() => {
                            const ts = u.location_updated_at + (u.location_updated_at.endsWith('Z') ? '' : 'Z');
                            const age = (Date.now() - new Date(ts).getTime()) / 60000;
                            return age <= 3 ? '#4caf50' : age <= 6 ? '#ff9800' : '#f44336';
                          })(),
                        }} />
                      )}
                      {u.current_location || ''}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {allUnits.filter((u: any) => u.is_logged_on).length === 0 && (
                <TableRow><TableCell colSpan={5} align="center" sx={{ color: 'text.secondary', py: 3 }}>No units logged on</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Tab 3: Calls */}
      {activeTab === 3 && (
        <TableContainer component={Paper}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>CAD #</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Priority</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Location</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Date/Time</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {allCalls.map((c: any) => (
                <TableRow key={c.id} hover>
                  <TableCell
                    sx={{ fontFamily: 'monospace', fontWeight: 700, color: 'primary.main', cursor: 'pointer', textDecoration: 'underline' }}
                    onClick={() => openCallDetail(c.id)}
                  >
                    {c.cad_number}
                  </TableCell>
                  <TableCell>{c.call_type_code || ''}</TableCell>
                  <TableCell>
                    <Chip size="small" label={`P${c.priority}`}
                      sx={{ bgcolor: PRIORITY_COLORS[c.priority] || '#9e9e9e', color: '#fff' }} />
                  </TableCell>
                  <TableCell>{c.location_address || ''}</TableCell>
                  <TableCell><Chip size="small" label={(c.status || '').toUpperCase()} /></TableCell>
                  <TableCell>{formatDateShort(c.created_at)} {formatTime24(c.created_at)}</TableCell>
                </TableRow>
              ))}
              {allCalls.length === 0 && (
                <TableRow><TableCell colSpan={6} align="center" sx={{ color: 'text.secondary', py: 3 }}>No calls</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Fixed bottom status bar */}
      {assigned_unit && (
        <Paper
          elevation={8}
          sx={{
            position: 'fixed', bottom: 0, left: 0, right: 0,
            p: 1, display: 'flex', gap: '4px',
            zIndex: 1300, bgcolor: '#1a1a2e',
          }}
        >
          {available_statuses.map((s) => (
            <Button
              key={s.id}
              variant={assigned_unit.status_id === s.id ? 'contained' : 'outlined'}
              onClick={() => s.code === 'OFF' ? logoffUnit() : changeStatus(s.id)}
              sx={{
                flex: 1, height: STATUS_BTN_SIZE,
                minWidth: 0,
                borderColor: s.color,
                color: assigned_unit.status_id === s.id ? '#fff' : s.color,
                bgcolor: assigned_unit.status_id === s.id ? s.color : 'transparent',
                '&:hover': { bgcolor: s.color, color: '#fff' },
                fontWeight: 700, fontSize: 12, lineHeight: 1.2,
                textAlign: 'center', whiteSpace: 'normal',
                px: 0.5,
              }}
            >
              {s.name}
            </Button>
          ))}
        </Paper>
      )}

      {/* Field Incident Dialog */}
      <Dialog open={fieldDialogOpen} onClose={() => setFieldDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Field Incident</DialogTitle>
        <DialogContent>
          {fieldError && <Alert severity="error" sx={{ mb: 2 }}>{fieldError}</Alert>}
          <Typography variant="subtitle2" sx={{ mt: 1, mb: 1, fontWeight: 700 }}>Incident Type</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {callTypes.filter((ct) => ct.available_for_field_incident).map((ct) => (
              <Button
                key={ct.id}
                variant={fieldForm.call_type_id === ct.id ? 'contained' : 'outlined'}
                color={fieldForm.call_type_id === ct.id ? 'primary' : 'inherit'}
                onClick={() => setFieldForm({ ...fieldForm, call_type_id: ct.id })}
                sx={{ minWidth: 100, minHeight: 48, fontSize: 14, textTransform: 'none' }}
              >
                {ct.code} - {ct.name}
              </Button>
            ))}
            {callTypes.filter((ct) => ct.available_for_field_incident).length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No call types configured for field incidents. Contact your administrator.
              </Typography>
            )}
          </Box>
          <TextField
            select fullWidth label="Priority" value={String(fieldForm.priority)}
            onChange={(e) => setFieldForm({ ...fieldForm, priority: Number(e.target.value) })}
            sx={{ mb: 2 }}
          >
            {[1,2,3,4,5].map((p) => <MenuItem key={p} value={String(p)}>P{p}</MenuItem>)}
          </TextField>
          <Autocomplete
            freeSolo
            options={locationResults}
            getOptionLabel={(opt) => typeof opt === 'string' ? opt : `@${opt.name} — ${opt.address}`}
            inputValue={fieldForm.location_address}
            onInputChange={(_e, value, reason) => {
              setFieldForm((f) => ({ ...f, location_address: value, ...(reason === 'input' ? { location_id: null } : {}) }));
              if (value.startsWith('@') && value.length > 1) {
                if (locationTimer.current) clearTimeout(locationTimer.current);
                locationTimer.current = setTimeout(() => {
                  api.get(`/locations/search?q=${encodeURIComponent(value.slice(1))}`)
                    .then((r) => setLocationResults(r.data)).catch(() => {});
                }, 300);
              } else { setLocationResults([]); }
            }}
            onChange={(_e, val) => {
              if (val && typeof val !== 'string') {
                const fullAddr = [val.address, val.city, val.state, val.zip_code].filter(Boolean).join(', ');
                let narrative = fieldForm.narrative;
                if (val.hazard_info) narrative = `*** HAZARD: ${val.hazard_info} ***\n${narrative}`;
                if (val.additional_notes) narrative = `${narrative}\nLocation Notes: ${val.additional_notes}`;
                setFieldForm((f) => ({
                  ...f, location_address: fullAddr,
                  location_apt: val.apt_suite || f.location_apt,
                  location_lat: val.latitude ?? null, location_lng: val.longitude ?? null,
                  location_id: val.id ?? null,
                  narrative: narrative.trim(),
                }));
              }
            }}
            renderOption={(props, opt) => (
              <li {...props} key={opt.id}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>@{opt.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{opt.address}{opt.city ? `, ${opt.city}` : ''}</Typography>
                  {opt.hazard_info && <Chip size="small" icon={<Warning />} label="Hazard" color="warning" sx={{ ml: 1 }} />}
                </Box>
              </li>
            )}
            sx={{ mb: 2 }}
            renderInput={(params) => (
              <TextField {...params} fullWidth label="Location / Address (type @ to search saved locations)"
                helperText="Leave blank to use unit's current TAIP/AVL location" />
            )}
          />
          <TextField
            fullWidth label="Initial Notes" value={fieldForm.narrative} multiline rows={3}
            onChange={(e) => setFieldForm({ ...fieldForm, narrative: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFieldDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={submitFieldIncident}>Create Incident</Button>
        </DialogActions>
      </Dialog>

      {/* Call Detail Dialog (read-only, from Calls tab) */}
      <Dialog open={callDetailOpen} onClose={() => setCallDetailOpen(false)} maxWidth="md" fullWidth>
        {selectedCallDetail && (
          <>
            <DialogTitle sx={{ fontWeight: 700 }}>
              {selectedCallDetail.cad_number} — {selectedCallDetail.call_type_name || selectedCallDetail.call_type_code || ''}
            </DialogTitle>
            <DialogContent>
              <Box sx={{ mb: 1 }}>
                <Chip size="small" label={`P${selectedCallDetail.priority}`}
                  sx={{ bgcolor: PRIORITY_COLORS[selectedCallDetail.priority] || '#9e9e9e', color: '#fff', mr: 1 }} />
                <Chip size="small" label={(selectedCallDetail.status || '').toUpperCase()} />
              </Box>
              {selectedCallDetail.location_address && (
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Location:</strong> {selectedCallDetail.location_address}
                  {selectedCallDetail.location_apt ? ` Apt ${selectedCallDetail.location_apt}` : ''}
                </Typography>
              )}
              {selectedCallDetail.cross_street && (
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>X-Street:</strong> {selectedCallDetail.cross_street}
                </Typography>
              )}
              {selectedCallDetail.reporting_party && (
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>RP:</strong> {selectedCallDetail.reporting_party}
                  {selectedCallDetail.reporting_phone ? ` — ${selectedCallDetail.reporting_phone}` : ''}
                </Typography>
              )}
              {selectedCallDetail.narrative && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Narrative</Typography>
                  <Paper sx={{ p: 1, bgcolor: '#0d0d1a', maxHeight: 300, overflow: 'auto' }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', fontSize: 13 }}>
                      {selectedCallDetail.narrative}
                    </Typography>
                  </Paper>
                </>
              )}
              {selectedCallDetail.premise_documents && selectedCallDetail.premise_documents.length > 0 && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Description fontSize="small" /> Premise Documents
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {selectedCallDetail.premise_documents.map((d: PremiseDocument) => (
                      <Button key={d.id} size="small" variant="outlined" startIcon={<OpenInNew />}
                        onClick={() => window.open(d.view_url, '_blank', 'noopener')}>
                        {(DOC_CATEGORY_LABELS[d.category] || 'Document')}{d.notes ? ` — ${d.notes}` : ''}
                      </Button>
                    ))}
                  </Box>
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setCallDetailOpen(false)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Clear/Disposition Dialog */}
      <Dialog open={clearDialogOpen} onClose={handleLeaveCallOpen} maxWidth="xs" fullWidth>
        <DialogTitle>All Units Cleared — {pendingClearCadNumber}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            You were the last unit on this call. Close the call or leave it open?
          </Typography>
          <TextField
            select fullWidth label="Disposition" size="small"
            value={selectedDisposition}
            onChange={(e) => setSelectedDisposition(e.target.value)}
          >
            {dispositions.map((d) => (
              <MenuItem key={d.id} value={d.name}>{d.code} — {d.name}</MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLeaveCallOpen}>Leave Open</Button>
          <Button variant="contained" color="error" disabled={!selectedDisposition} onClick={handleCloseCall}>
            Close Call
          </Button>
        </DialogActions>
      </Dialog>

      {/* Field Incident Created Confirmation */}
      <Dialog open={fieldConfirmOpen} onClose={() => {}} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ textAlign: 'center' }}>Field Incident Created</DialogTitle>
        <DialogContent sx={{ textAlign: 'center', py: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>{fieldConfirmCad}</Typography>
          <Typography variant="body2" color="text.secondary">Your field incident has been submitted successfully.</Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
          <Button
            variant="contained"
            disabled={fieldConfirmCountdown > 0}
            onClick={() => setFieldConfirmOpen(false)}
          >
            {fieldConfirmCountdown > 0 ? `OK (${fieldConfirmCountdown})` : 'OK'}
          </Button>
        </DialogActions>
      </Dialog>


    </Box>
  );
}

/* Inline Map component for MDT Map tab */
function MdtMapView({ units, calls }: { units: any[]; calls: any[] }) {
  const mapRef = React.useRef<HTMLDivElement>(null);
  const mapInstanceRef = React.useRef<any>(null);

  React.useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    const L = (window as any).L;
    if (!L) {
      // Load Leaflet dynamically
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.onload = () => initMap();
      document.head.appendChild(script);
    } else {
      initMap();
    }

    function initMap() {
      const L2 = (window as any).L;
      if (!L2 || !mapRef.current) return;
      const map = L2.map(mapRef.current, { maxZoom: 20 }).setView([37.2, -93.3], 12);
      const streets = L2.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 20, attribution: 'OSM',
      });
      const dark = L2.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
        maxZoom: 20, attribution: '&copy; OpenStreetMap &copy; CARTO',
      });
      const satellite = L2.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
        attribution: 'Esri', maxZoom: 20,
      });
      const layers: Record<string, any> = { 'Street': streets, 'Dark': dark, 'Satellite': satellite };
      const savedLayer = localStorage.getItem('mdt_map_layer') || 'Street';
      (layers[savedLayer] || streets).addTo(map);
      L2.control.layers(layers, {}, { position: 'topright' }).addTo(map);
      map.on('baselayerchange', (e: any) => { localStorage.setItem('mdt_map_layer', e.name); });
      mapInstanceRef.current = map;

      // Try to get configured center
      fetch('/api/system-config/public')
        .then((r: Response) => r.json())
        .then((cfg: any) => {
          if (cfg.map_center_lat && cfg.map_center_lng) {
            map.setView([cfg.map_center_lat, cfg.map_center_lng], cfg.map_default_zoom || 12);
          }
        }).catch(() => {});
    }

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  // Update markers when units/calls change
  React.useEffect(() => {
    const L = (window as any).L;
    const map = mapInstanceRef.current;
    if (!L || !map) return;

    // Clear existing markers
    map.eachLayer((layer: any) => {
      if (layer instanceof L.Marker || layer instanceof L.CircleMarker) {
        map.removeLayer(layer);
      }
    });

    // Add unit markers
    const z = map.getZoom() || 13;
    const scale = Math.max(0.6, Math.min(2.5, (z - 6) / 6));
    const imgSize = Math.round(40 * scale);
    const fontSize = Math.round(12 * scale);
    const pad = Math.max(2, Math.round(4 * scale));
    units.filter((u: any) => u.is_logged_on && u.location_lat && u.location_lng).forEach((u: any) => {
      const typeIcon = u.unit_type_icon
        ? `<img src="${escapeHtml(u.unit_type_icon)}" style="width:${imgSize}px;height:${imgSize}px;object-fit:contain;display:block;margin:0 auto;" />`
        : '';
      const icon = L.divIcon({
        className: '',
        html: `<div style="text-align:center;">${typeIcon}<div style="background:${safeColor(u.status_color)};color:#fff;padding:${pad}px ${pad * 2}px;border-radius:4px;font-size:${fontSize}px;font-weight:700;white-space:nowrap;border:1px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.4);display:inline-block">${escapeHtml(u.callsign)}</div></div>`,
        iconSize: [0, 0],
        iconAnchor: [0, 0],
      });
      L.marker([u.location_lat, u.location_lng], { icon })
        .addTo(map)
        .bindPopup(`<b>${escapeHtml(u.callsign)}</b><br/>Status: ${escapeHtml(u.status_name)}<br/>${escapeHtml(u.current_location)}`);
    });

    // Add call markers
    calls.filter((c: any) => c.location_lat && c.location_lng && c.status !== 'closed').forEach((c: any) => {
      const color = PRIORITY_COLORS[c.priority] || '#9e9e9e';
      const icon = L.divIcon({
        className: '',
        html: `<div style="background:${color};color:#fff;padding:2px 6px;border-radius:50%;font-size:11px;font-weight:700;width:24px;height:24px;display:flex;align-items:center;justify-content:center;border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.4)">!</div>`,
        iconAnchor: [14, 14],
      });
      L.marker([c.location_lat, c.location_lng], { icon })
        .addTo(map)
        .bindPopup(`<b>${escapeHtml(c.cad_number)}</b><br/>${escapeHtml(c.call_type_code)} - P${escapeHtml(c.priority)}<br/>${escapeHtml(c.location_address)}`);
    });
  }, [units, calls]);

  return <div ref={mapRef} style={{ width: '100%', height: '100%' }} />;
}

/* Focused map for the assigned call: call location + this unit's AVL position */
function MdtCallMap({ call, unit }: { call: CallInfo; unit: UnitInfo | null }) {
  const mapRef = React.useRef<HTMLDivElement>(null);
  const mapInstanceRef = React.useRef<any>(null);
  const markerLayerRef = React.useRef<any>(null);

  React.useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    const ensureLeaflet = (cb: () => void) => {
      const L = (window as any).L;
      if (L) { cb(); return; }
      if (!document.querySelector('link[data-leaflet]')) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        link.setAttribute('data-leaflet', '1');
        document.head.appendChild(link);
      }
      let script = document.querySelector('script[data-leaflet]') as HTMLScriptElement | null;
      if (!script) {
        script = document.createElement('script');
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        script.setAttribute('data-leaflet', '1');
        document.head.appendChild(script);
      }
      script.addEventListener('load', cb);
    };

    ensureLeaflet(() => {
      const L = (window as any).L;
      if (!L || !mapRef.current || mapInstanceRef.current) return;
      const map = L.map(mapRef.current, { maxZoom: 20, zoomControl: true }).setView(
        [call.location_lat as number, call.location_lng as number], 16,
      );
      const streets = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 20, attribution: 'OSM' });
      const dark = L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', { maxZoom: 20, attribution: '&copy; OpenStreetMap &copy; CARTO' });
      const satellite = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', { attribution: 'Esri', maxZoom: 20 });
      const layers: Record<string, any> = { 'Street': streets, 'Dark': dark, 'Satellite': satellite };
      const savedLayer = localStorage.getItem('mdt_map_layer') || 'Street';
      (layers[savedLayer] || streets).addTo(map);
      L.control.layers(layers, {}, { position: 'topright' }).addTo(map);
      map.on('baselayerchange', (e: any) => { localStorage.setItem('mdt_map_layer', e.name); });
      mapInstanceRef.current = map;
      // Force a resize once the container settles in the grid
      setTimeout(() => map.invalidateSize(), 200);
    });

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Update markers whenever the call or unit location changes
  React.useEffect(() => {
    const L = (window as any).L;
    const map = mapInstanceRef.current;
    if (!L || !map) return;

    if (markerLayerRef.current) { map.removeLayer(markerLayerRef.current); }
    const group = L.layerGroup().addTo(map);
    markerLayerRef.current = group;

    const pts: [number, number][] = [];

    if (call.location_lat && call.location_lng) {
      const color = PRIORITY_COLORS[call.priority] || '#9e9e9e';
      const callIcon = L.divIcon({
        className: '',
        html: `<div style="background:${color};color:#fff;padding:2px 6px;border-radius:50%;font-size:12px;font-weight:700;width:26px;height:26px;display:flex;align-items:center;justify-content:center;border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.4)">!</div>`,
        iconAnchor: [15, 15],
      });
      L.marker([call.location_lat, call.location_lng], { icon: callIcon })
        .addTo(group)
        .bindPopup(`<b>${escapeHtml(call.cad_number)}</b><br/>${escapeHtml(call.call_type_code)} - P${escapeHtml(call.priority)}<br/>${escapeHtml(call.location_address)}`);
      pts.push([call.location_lat, call.location_lng]);
    }

    if (unit && unit.location_lat && unit.location_lng) {
      const unitIcon = L.divIcon({
        className: '',
        html: `<div style="background:${safeColor(unit.status_color)};color:#fff;padding:2px 8px;border-radius:4px;font-size:12px;font-weight:700;white-space:nowrap;border:1px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.4);display:inline-block">${escapeHtml(unit.callsign)}</div>`,
        iconSize: [0, 0],
        iconAnchor: [0, 0],
      });
      L.marker([unit.location_lat, unit.location_lng], { icon: unitIcon })
        .addTo(group)
        .bindPopup(`<b>${escapeHtml(unit.callsign)}</b> (you)<br/>${escapeHtml(unit.status_name)}`);
      pts.push([unit.location_lat, unit.location_lng]);
    }

    if (pts.length > 1) {
      map.fitBounds(pts, { padding: [50, 50], maxZoom: 17 });
    } else if (pts.length === 1) {
      map.setView(pts[0], 16);
    }
  }, [call.location_lat, call.location_lng, call.cad_number, call.priority,
      unit?.location_lat, unit?.location_lng, unit?.callsign, unit?.status_color]);

  return <div ref={mapRef} style={{ width: '100%', height: '100%', minHeight: 320 }} />;
}
